import {Helmet} from '@dr.pogodin/react-helmet';
import {closeDialog, FlagProps, openDialog, showNotification} from 'platform/components';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetDocumentContextCountQuery} from '@dms/api/core';
import {
  useGetSupplierOrderQuery,
  useGetWarehouseHeaderDynamicActionsQuery,
  usePostPrintoutOnDemandMutation,
  usePutSupplierOrderHeaderActionMutation,
} from '@dms/api/warehouse';
import {featureFlags} from '@dms/feature-flags';
import {useRefreshDocumentsCount} from '@dms/features/document-context';
import i18n from '@dms/i18n';
import {testIds, warehouseRoutes} from '@dms/routes';
import {
  Checklists,
  DetailTemplate,
  DetailTemplateHeader,
  handleApiError,
  NavigationItem,
  createHeaderActions,
  Section,
  useChecklist,
  usePermissions,
  printPDF,
  useFileNotification,
} from '@dms/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {buildArray, composePath, generateHashFromObjects, useNavigate} from 'shared/web';

import {Documents} from './(sections)/Documents/Documents';
import {Overview} from './(sections)/Overview/Overview';
import {BulkPackageVerificationPrompt} from './components/BulkPackageVerificationPrompt';
import {BULK_PACKAGE_VERIFICATION_PROMPT} from './constants/BulkPackageVerificationPrompt';

export function SupplierOrderDetail() {
  const {supplierOrderId} = useWarehouseParams();
  const fileNotification = useFileNotification({timeoutMs: 30000});

  const navigate = useNavigate();

  const [supplierOrderEdit] = usePermissions({
    permissionKeys: ['supplierOrderEdit'],
  });

  const {
    data: supplierOrder,
    isLoading: isSupplierOrderLoading,
    isFetching: isSupplierOrderFetching,
    isError: isSupplierOrderError,
  } = useGetSupplierOrderQuery({orderId: supplierOrderId}, {skip: isNilOrEmpty(supplierOrderId)});

  const {
    data: supplierOrderActions,
    isLoading: isSupplierOrderActionsLoading,
    isError: isSupplierOrderActionsError,
  } = useGetWarehouseHeaderDynamicActionsQuery({
    id: supplierOrderId,
    resource: 'supplier-order',
    version: 'v2',
  });

  const documentCountQuery = useGetDocumentContextCountQuery(
    {contextTarget: 'warehouse-supplier-order', contextId: supplierOrderId},
    {skip: !supplierOrderId}
  );

  const refreshDocumentsCount = useRefreshDocumentsCount({
    contextTarget: 'warehouse-supplier-order',
    contextId: supplierOrderId,
  });

  const [putSupplierOrderAction, {isLoading: isPutSupplierOrderActionLoading}] =
    usePutSupplierOrderHeaderActionMutation();

  const {badgeSettings, checklistSettings} = useChecklist({
    recordId: supplierOrderId,
    resourceId: 'SUPPLIER_ORDER',
  });

  const [postPrintoutOnDemand, {isLoading: isPostPrintoutOnDemandLoading}] =
    usePostPrintoutOnDemandMutation();

  const isLoading = isSupplierOrderLoading || isSupplierOrderActionsLoading;
  const isError = isSupplierOrderError || isSupplierOrderActionsError;

  const handleNavigateBack = () => {
    navigate(composePath(warehouseRoutes.supplierOrderList));
  };

  const isPending = supplierOrder?.order?.state === 'PENDING';
  const isSent = supplierOrder?.order?.state === 'SENT';
  const isClosed = supplierOrder?.order?.state === 'CLOSED';
  const isCancelled = supplierOrder?.order?.state === 'CANCELLED';

  const headerFlags = buildArray<FlagProps>()
    .when(isPending, {
      colorScheme: 'blue',
      label: i18n.t('general.labels.pending'),
    })
    .when(isSent, {
      colorScheme: 'yellow',
      label: i18n.t('general.labels.sent'),
    })
    .when(isClosed, {
      colorScheme: 'green',
      label: i18n.t('entity.warehouse.labels.closedState'),
    })
    .when(isCancelled, {
      colorScheme: 'red',
      label: i18n.t('general.labels.cancelled'),
    });

  const handleActionClick = (actionKey: string) => {
    if (actionKey === 'prntSupplierOrder') {
      return postPrintoutOnDemand({
        body: {
          docOriginId: supplierOrderId,
          originEntityPrintoutType: 'prntSupplierOrder',
        },
      })
        .unwrap()
        .then((data) => {
          const fileId = data?.printout?.fileId;
          const pdfUrl = data?.printout?.pdfUrl;

          if (fileId) {
            fileNotification.registerFile(fileId, {
              onSuccess: () => {
                if (pdfUrl) {
                  printPDF(pdfUrl);
                } else {
                  showNotification.error();
                }
              },
            });
          } else {
            showNotification.error();
          }
        })
        .then(refreshDocumentsCount)
        .then(() =>
          showNotification.success(i18n.t('entity.warehouse.labels.documentWasGenerated'))
        )
        .then(() => {
          navigate(
            composePath(warehouseRoutes.supplierOrderDetailDocuments, {
              params: {id: supplierOrderId},
            })
          );
        })
        .catch(handleApiError);
    }

    return putSupplierOrderAction({
      orderId: supplierOrderId,
      body: {
        actionKey,
      },
    })
      .unwrap()
      .then((result) => {
        // DELETE removes the supplier order, so its detail page no longer exists
        if (actionKey === 'DELETE') {
          handleNavigateBack();
          return;
        }

        if (result?.normalizePrompt) {
          return openDialog(
            <BulkPackageVerificationPrompt
              onCompleteVerification={(code) =>
                putSupplierOrderAction({
                  orderId: supplierOrderId,
                  body: {actionKey, normalizePromptResponseCode: code},
                })
                  .unwrap()
                  .then(() => closeDialog(BULK_PACKAGE_VERIFICATION_PROMPT))
                  .then(refreshDocumentsCount)
                  .catch(handleApiError)
              }
              data-testid={testIds.warehouse.supplierOrderDetailOverview(
                'bulkPackageVerificationPrompt'
              )}
            />,
            {
              id: BULK_PACKAGE_VERIFICATION_PROMPT,
              title: i18n.t('entity.warehouse.labels.bulkPackageVerification'),
              size: 'small',
            }
          );
        }

        refreshDocumentsCount();
      })
      .catch(handleApiError);
  };

  const headerActions = createHeaderActions({
    actions: supplierOrderActions?.actions,
    callback: handleActionClick,
    isLoading: isPutSupplierOrderActionLoading || isPostPrintoutOnDemandLoading,
  });

  const header: DetailTemplateHeader = {
    title: `${i18n.t('entity.warehouse.labels.supplierOrder')} ${supplierOrder?.order?.orderNumber ?? ''}`,
    isEntityLoading: isSupplierOrderFetching,
    controls: ['ASSIGNEE'],
    flags: headerFlags,
    actions: headerActions,
    alerts: !supplierOrderEdit
      ? [
          {
            type: 'banner',
            variant: 'info',
            message: i18n.t('module.warehouse.label.missingSupplierOrderEditPermission'),
          },
        ]
      : undefined,
  };

  const navigation = buildArray<NavigationItem>([
    {
      id: 'supplierOrderDetail',
      label: i18n.t('page.warehouse.labels.overview'),
      href: composePath(warehouseRoutes.supplierOrderDetailOverview, {
        params: {id: supplierOrderId},
      }),
      content: (
        <Overview
          data-testid={testIds.warehouse.supplierOrderDetailOverview('section.overview')}
          supplierOrder={supplierOrder}
          isPending={isPending}
          isOrderLoading={isSupplierOrderLoading}
          hasOrderError={isSupplierOrderError}
          isReadOnly={isSent || isClosed || isCancelled}
        />
      ),
      'data-testid': testIds.warehouse.supplierOrderDetailOverview('navigation.supplierOrder'),
    },
    {
      id: 'supplierOrderDocuments',
      label: i18n.t('page.warehouse.labels.documents'),
      href: composePath(warehouseRoutes.supplierOrderDetailDocuments, {
        params: {id: supplierOrderId},
      }),
      content: (
        <Documents
          onDocumentAction={refreshDocumentsCount}
          data-testid={testIds.warehouse.supplierOrderDetailOverview('section.documents')}
        />
      ),
      badge: documentCountQuery.data?.count
        ? {value: documentCountQuery.data.count, colorScheme: 'neutral'}
        : undefined,
      'data-testid': testIds.warehouse.supplierOrderDetailOverview('navigation.documents'),
    },
  ]).whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, () => ({
    id: 'supplierOrderChecklists',
    label: i18n.t('entity.checklist.labels.title'),
    href: composePath(warehouseRoutes.supplierOrderDetailChecklists, {
      params: {id: supplierOrderId},
    }),
    content: (
      <Section>
        <Checklists
          {...checklistSettings}
          contextTarget="warehouse-supplier-order"
          contextId={supplierOrderId}
        />
      </Section>
    ),
    badge: badgeSettings,
    'data-testid': testIds.warehouse.supplierOrderDetailChecklists('checklists'),
  }));

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.supplierOrder')} />
      <DetailTemplate
        key={generateHashFromObjects(supplierOrder)}
        isLoading={isLoading}
        isError={isError}
        header={header}
        navigation={navigation}
        isLocked={isPutSupplierOrderActionLoading}
        data-testid={testIds.warehouse.supplierOrderDetailOverview('page')}
      />
    </>
  );
}
